import React, { Dispatch, JSX, SetStateAction, useState } from "react";
import { TriangleAlert, ChevronDown, ChevronUp, InfoIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { gql, useQuery, useMutation } from "@apollo/client";
import {
  createSearchParams,
  generatePath,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Formik } from "formik";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NameValidationIcon from "@/assets/NameValidationIcon";
import GreenCheck from "@/assets/green-check.svg";
import ProjectSelector from "@/components/ProjectSelector/ProjectSelector";
import ServiceDots, {
  formatServiceName,
  getServiceOrSubserviceBasedOnSubservice,
} from "../ServiceDots";
import {
  AutoNamingRulesForTaskShotQuery,
  AutoNamingRulesForTaskShotQueryVariables,
  ValidateFilesForAutoNamingMutation,
  ValidateFilesForAutoNamingMutationVariables,
  ZippedFilesTaskShotInput,
} from "@/__generated__/graphql";
import { AUTO_NAMING_RULES_QUERY } from "@/components/Actions/Deliver/LinkMedia";
import { useUserContext } from "@/contexts/UserContextProvider";
import { getArrayFilterFromParams } from "@/helpers/utils";
import { TASK_SHOTS_QUERY_PARAM } from "@/helpers/constants";
import { Loader } from "../Loader";
import { ACTIONS_URL } from "@/helpers/url";
import SelectLocalFolderField from "../SelectLocalFolderField/SelectLocalFolderField";
import LongContentWrapper from "../LongContentWrapper";
import JSZip from "jszip";
import { isFile } from "@/helpers/filename";
import { ActionsType } from "@/components/Actions/Actions.type";

const VALIDATE_FILES_FOR_AUTO_NAMING = gql`
  mutation ValidateFilesForAutoNaming(
    $projectId: SmartID!
    $zippedFilesByTaskShotIds: [ZippedFilesTaskShotInput]!
    $taskShotIds: [SmartID]!
    $subRuleName: String
  ) {
    validateFilesForAutoNaming(
      projectId: $projectId
      zippedFilesByTaskShotIds: $zippedFilesByTaskShotIds
      taskShotIds: $taskShotIds
      subRuleName: $subRuleName
    ) {
      success
      message
      autoNamingErrors {
        taskShotName
        errors
      }
    }
  }
`;

interface UploadMediaErrorsProps {
  errors: string[];
  htmlError?: JSX.Element[];
}

const UploadMediaErrors: React.FC<UploadMediaErrorsProps> = ({
  errors,
  htmlError,
}) => (
  <div className="flex flex-col w-full max-w-full">
    <div className="space-y-2 p-2">
      {htmlError &&
        htmlError.map((err, index) => (
          <div
            key={`html-error-${index}`}
            className="text-red-500 text-sm overflow-wrap break-words max-w-full"
          >
            {err}
          </div>
        ))}
      {!htmlError &&
        errors.map((error, index) => (
          <div
            key={`error-${index}`}
            className="text-red-500 text-sm overflow-wrap break-words max-w-full"
          >
            {error}
          </div>
        ))}
    </div>
  </div>
);

type AutoNamingRuleItem = NonNullable<
  NonNullable<
    AutoNamingRulesForTaskShotQuery["autoNamingRulesForTaskShot"]
  >[number]
>;

type ServiceRule = NonNullable<AutoNamingRuleItem["serviceRules"]>[number];

interface FileUploaderProps {
  projectId: string;
  taskShotId: string;
  serviceName: string;
  callback: () => void;
  taskShotIds: string[];
  selectedSubrule?: string;
  namingErrors:
    | NonNullable<
        NonNullable<
          ValidateFilesForAutoNamingMutation["validateFilesForAutoNaming"]
        >["autoNamingErrors"]
      >
    | [];
  setNamingErrors: Dispatch<
    SetStateAction<
      | NonNullable<
          NonNullable<
            ValidateFilesForAutoNamingMutation["validateFilesForAutoNaming"]
          >["autoNamingErrors"]
        >
      | []
    >
  >;
  isFileValid: boolean;
  setIsFileValid: Dispatch<SetStateAction<boolean>>;
}

const NameValidationFileUpload: React.FC<FileUploaderProps> = ({
  projectId,
  taskShotId,
  serviceName,
  callback,
  taskShotIds,
  selectedSubrule,
  namingErrors,
  setNamingErrors,
  isFileValid,
  setIsFileValid,
}) => {
  const [info, setInfo] = useState<"message" | "error">("message");

  const [validateFilesForAutoNaming] = useMutation<
    ValidateFilesForAutoNamingMutation,
    ValidateFilesForAutoNamingMutationVariables
  >(VALIDATE_FILES_FOR_AUTO_NAMING);

  const INITIAL_VALUES = {
    zippedFilesByTaskShotIds: [],
    projectId: "",
    taskShotIds: [],
    selectedSubrule: "",
  };

  const bytesToGB = (bytes: number) => {
    return (bytes / 1024 ** 3).toFixed(1);
  };

  return (
    <div className="flex flex-col w-full max-w-full">
      <div
        className={cn(
          info === "error"
            ? "text-destructive bg-destructive/20"
            : "text-service-3d-tracking bg-service-3d-tracking/20",
          "rounded-lg py-3 px-5 text-xs mb-4 max-w-full overflow-wrap break-words",
        )}
      >
        {info === "message" ? (
          "You can drag and drop folders here. No zips required"
        ) : (
          <div className="flex items-center gap-4">
            <TriangleAlert className="w-10 h-10 flex-shrink-0" />
            <span className="overflow-wrap break-words">
              We detected the following issues in the delivery package. Please
              resolve the issues and redeliver the package
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-col w-full max-w-full">
        <Formik
          initialValues={INITIAL_VALUES}
          onSubmit={async (values, { setSubmitting }) => {
            validateFilesForAutoNaming({
              variables: {
                zippedFilesByTaskShotIds: values.zippedFilesByTaskShotIds,
                projectId,
                taskShotIds,
                subRuleName: selectedSubrule,
              },
            })
              .then((data) => {
                setSubmitting(false);
                if (
                  data.data?.validateFilesForAutoNaming?.autoNamingErrors
                    ?.length
                ) {
                  setNamingErrors(
                    data.data.validateFilesForAutoNaming.autoNamingErrors,
                  );
                  setInfo("error");
                } else {
                  setIsFileValid(true);
                }
              })
              .catch(() => setSubmitting(false));
          }}
        >
          {({ isSubmitting, setFieldValue, submitForm, setSubmitting }) => (
            <>
              {!namingErrors.length && !isFileValid && (
                <SelectLocalFolderField
                  onSuccess={async (files) => {
                    setSubmitting(true);
                    const zippedFilesByTaskShotIds: ZippedFilesTaskShotInput[] =
                      [];
                    const zippedFiles: Array<string> = [];
                    const filesIsValid = files.every((f) =>
                      f.name.includes("zip") ? +bytesToGB(f.size) <= 1.9 : true,
                    );
                    if (!files.every((f) => f.size > 0)) {
                      callback();
                      toast.error(
                        "One or more files in the delivery package are of 0 bytes. Please recheck.",
                      );
                    }
                    if (filesIsValid) {
                      for (const file of files) {
                        if (file.name.includes(".zip")) {
                          const parent_dir = file.name.split("/")[0];
                          const zipFile = file;
                          const zip = new JSZip();
                          const zipContent = await zip.loadAsync(zipFile);
                          zippedFiles.push(
                            ...Object.values(zipContent.files)
                              .map((value) => `${parent_dir}/${value.name}`)
                              .filter(
                                (name) =>
                                  !!isFile(name) &&
                                  !name.includes("__MACOSX") &&
                                  !name.includes("DS_Store"),
                              ),
                          );
                        } else {
                          zippedFiles.push(file.name);
                        }
                      }
                      zippedFilesByTaskShotIds.push({
                        taskShotId,
                        zippedFiles,
                      });
                      await setFieldValue(
                        "zippedFilesByTaskShotIds",
                        zippedFilesByTaskShotIds,
                      );
                      setTimeout(() => {
                        submitForm();
                      }, 0);
                    } else {
                      callback();
                      toast.error(
                        "Zipped file more than 2GB is invalid please upload unzipped folder",
                      );
                    }
                  }}
                />
              )}

              {!!namingErrors.length && (
                <div className="max-h-64 overflow-y-auto max-w-full pr-2">
                  <UploadMediaErrors
                    errors={[]}
                    htmlError={namingErrors.map((e, index) => (
                      <div
                        key={`naming-error-${index}`}
                        className="flex flex-col max-w-full"
                      >
                        {e?.errors?.map((er, i) => (
                          <div
                            key={`error-${i}`}
                            className="flex flex-col max-w-full overflow-wrap break-words gap-2"
                          >
                            <span className="max-w-full">
                              {i === 0 ? (
                                <strong>
                                  <LongContentWrapper
                                    tooltipPlacement="top"
                                    className="inline-block max-w-full overflow-wrap break-words"
                                  >
                                    Shot: {` ${e.taskShotName} `}
                                  </LongContentWrapper>{" "}
                                </strong>
                              ) : (
                                ""
                              )}
                            </span>
                            <span className="text-secondary-foreground flex flex-col gap-2 max-w-full overflow-wrap break-words">
                              {i === 0
                                ? er
                                : er.replace("please upload delivery with", "")}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))}
                  />
                </div>
              )}

              {isFileValid && !namingErrors.length && !isSubmitting && (
                <div className="flex flex-col items-center max-w-full">
                  <img
                    src={GreenCheck}
                    alt="green check"
                    className="w-16 h-16 mb-2"
                  />
                  <span className="text-center overflow-wrap break-words">
                    <strong>Wohoo!</strong> Your <strong>{serviceName}</strong>{" "}
                    <span>{`${taskShotIds.length > 1 ? "tasks" : "task"}`}</span>{" "}
                    look good.
                  </span>
                </div>
              )}
            </>
          )}
        </Formik>
      </div>
    </div>
  );
};

const CheckDeliveryModal = ({
  openNameValidationModal,
  setOpenNameValidationModal,
  serviceName,
  projectId,
  taskShotId,
  taskShotIds,
  selectedSubrule,
}: {
  openNameValidationModal: boolean;
  setOpenNameValidationModal: Dispatch<SetStateAction<boolean>>;
  serviceName: string;
  projectId: string;
  taskShotId: string;
  taskShotIds: string[];
  selectedSubrule?: string;
}) => {
  const contentKey = openNameValidationModal
    ? `${taskShotId}-open`
    : `${taskShotId}-closed`;
  const [namingErrors, setNamingErrors] = useState<
    | NonNullable<
        NonNullable<
          ValidateFilesForAutoNamingMutation["validateFilesForAutoNaming"]
        >["autoNamingErrors"]
      >
    | []
  >([]);
  const [isFileValid, setIsFileValid] = useState<boolean>(false);

  return (
    <Dialog
      open={openNameValidationModal}
      onOpenChange={setOpenNameValidationModal}
    >
      <DialogContent className="max-h-[80vh] max-w-full sm:max-w-lg w-full p-6 flex flex-col">
        <span className="text-lg font-semibold overflow-wrap break-words">
          Check Your {serviceName} Delivery
        </span>
        <Separator />
        <div
          key={contentKey}
          className="mt-2 flex-1 overflow-y-auto max-w-full"
        >
          <NameValidationFileUpload
            taskShotId={taskShotId}
            projectId={projectId}
            serviceName={serviceName}
            callback={() => setOpenNameValidationModal(false)}
            taskShotIds={taskShotIds}
            selectedSubrule={selectedSubrule}
            namingErrors={namingErrors}
            setNamingErrors={setNamingErrors}
            isFileValid={isFileValid}
            setIsFileValid={setIsFileValid}
          />
        </div>
        <Separator />
        <DialogFooter className="mt-2 shrink-0 flex justify-end gap-2">
          <Button onClick={() => setOpenNameValidationModal(false)}>
            {namingErrors.length && isFileValid ? "Check Another" : "Close"}
          </Button>
          <Button
            className="bg-paint"
            onClick={() => setOpenNameValidationModal(false)}
          >
            {namingErrors.length && isFileValid ? "Okay! Got it" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface NameValidatorListProps {
  filteredTaskShots: AutoNamingRuleItem[];
  taskShotIds: string[];
  openNameValidationModal: boolean;
  setOpenNameValidationModal: Dispatch<SetStateAction<boolean>>;
  selectedDelivery: {
    taskShotId: string;
    selectedSubrule?: string;
    serviceName: string;
    projectId: string;
  } | null;
  setSelectedDelivery: Dispatch<
    SetStateAction<{
      taskShotId: string;
      selectedSubrule?: string;
      serviceName: string;
      projectId: string;
    } | null>
  >;
  projectId: string;
}

const getDefaultValue = (
  serviceRules:
    | NonNullable<AutoNamingRuleItem["serviceRules"]>
    | null
    | undefined,
) => {
  if (!serviceRules || !serviceRules[0]) return undefined;
  const first = serviceRules[0];
  if (first.rules && first.rules.length > 1) return undefined;
  if (Array.isArray(first.subRuleNames) && first.subRuleNames.length > 0) {
    return first.subRuleNames[0] as string;
  }
  return undefined;
};

const NameValidatorList = ({
  filteredTaskShots,
  taskShotIds,
  openNameValidationModal,
  setOpenNameValidationModal,
  selectedDelivery,
  setSelectedDelivery,
  projectId,
}: NameValidatorListProps) => {
  const getFormats = (
    formatJson: NonNullable<
      NonNullable<ServiceRule["mandatoryFormats"]>[number]
    >,
    index: number,
    nonMandatory?: boolean,
  ) => {
    if (!formatJson) return null;

    const subFormat = formatJson.subFormat;

    return (
      <div key={`format-${index}`} className="max-w-full">
        <li
          className={cn("flex items-center mb-1", `ele${index}`, {
            "text-gray-500": nonMandatory,
            "text-red-500": !subFormat?.length && !nonMandatory,
          })}
        >
          <span className="flex items-center overflow-wrap break-words">
            <strong>{formatJson.count}</strong>
            &nbsp;
            <span>{formatJson.format}</span>
          </span>
          {!subFormat?.length && formatJson.description && (
            <span className="ml-2 flex-shrink-0">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="w-4 h-4" />
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    className="!p-2 !bg-primary border border-accent-foreground max-w-xs overflow-wrap break-words"
                  >
                    {formatJson.description}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </span>
          )}
        </li>
        {subFormat?.map((sf, sfIndex) => (
          <div key={`subformat-${sfIndex}`} className="ml-4 max-w-full">
            <span className="text-gray-500">-</span>
            &nbsp;
            <span
              className={cn(
                sf.mandatory ? "text-red-500" : "text-gray-500",
                "overflow-wrap break-words",
              )}
            >
              {sf?.name?.replace(/'/g, "")}
            </span>
            &nbsp;
            {sf.description && (
              <span className="ml-2 flex-shrink-0">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InfoIcon className="w-4 h-4" />
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      className="!p-2 !bg-primary border border-accent-foreground max-w-xs overflow-wrap break-words"
                    >
                      {sf.description}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };
  const [openId, setOpenId] = useState<string | null>(null);
  const [subRuleByTaskShot, setSubRuleByTaskShot] = useState<
    Record<string, string | undefined>
  >({});

  React.useEffect(() => {
    if (!filteredTaskShots.length) return;

    setSubRuleByTaskShot((prev) => {
      let changed = false;
      const updated = { ...prev };

      for (const ts of filteredTaskShots) {
        if (updated[ts.id] !== undefined) continue;
        const sr = (ts.serviceRules ?? []).filter(
          (r): r is ServiceRule => r !== null,
        );
        const def = getDefaultValue(sr);
        if (updated[ts.id] !== def) {
          updated[ts.id] = def;
          changed = true;
        }
      }

      return changed ? updated : prev;
    });
  }, [filteredTaskShots]);

  return (
    <div className="max-w-full">
      {filteredTaskShots.map((autoNamingRule, idx) => {
        const serviceRules = (autoNamingRule.serviceRules ?? []).filter(
          (sr): sr is ServiceRule => sr !== null,
        );
        const subRule = subRuleByTaskShot[autoNamingRule.id];

        if (!serviceRules[0]?.subRuleNames) {
          return null;
        }

        return (
          <div key={idx} className="my-5 border-2 rounded-xl px-10 max-w-full">
            <ServiceDots
              capitalize
              services={getServiceOrSubserviceBasedOnSubservice([
                autoNamingRule.task.service,
              ])}
              flatTop
            />
            <div className="max-w-full">
              {serviceRules[0]?.subRuleNames?.length > 1 && (
                <div className="max-w-full mt-5">
                  <div className="text-sm text-secondary-foreground">
                    Delivery Type
                  </div>
                  <div className="mt-2">
                    <DropdownMenu
                      open={openId === autoNamingRule.id}
                      onOpenChange={(val) =>
                        setOpenId(
                          val
                            ? autoNamingRule.id
                            : openId === autoNamingRule.id
                              ? null
                              : openId,
                        )
                      }
                    >
                      <DropdownMenuTrigger className="w-150 cursor-pointer max-w-full text-primary-foreground flex items-center justify-between border border-border px-4 py-2 rounded-md bg-tertiary">
                        {subRule || "Select a delivery type"}
                        {openId === autoNamingRule.id ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground ml-auto" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground ml-auto" />
                        )}
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-150 max-w-full bg-accordion border border-border rounded-md shadow-lg max-h-64 overflow-y-auto">
                        {serviceRules[0]?.subRuleNames?.length ? (
                          serviceRules[0].subRuleNames.map(
                            (subRuleName, index) => {
                              return (
                                <DropdownMenuItem
                                  key={`subrule-${index}`}
                                  onSelect={() => {
                                    setSubRuleByTaskShot((prev) => ({
                                      ...prev,
                                      [autoNamingRule.id]: subRuleName,
                                    }));
                                    setOpenId(null);
                                  }}
                                  className={cn(
                                    "px-4 py-2 text-sm text-primary-foreground hover:bg-accent cursor-pointer break-words",
                                    subRule === subRuleName &&
                                      "bg-accent-foreground dark:bg-accent",
                                  )}
                                >
                                  {subRuleName}
                                </DropdownMenuItem>
                              );
                            },
                          )
                        ) : (
                          <DropdownMenuItem className="px-4 py-2 text-sm text-gray-500 cursor-default">
                            No delivery types available
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              )}
              <div className="flex mt-5 flex-col md:flex-row justify-center gap-7 max-w-full">
                {subRule && (
                  <div className="flex-1">
                    <span className="text-lg font-semibold overflow-wrap break-words">
                      Files (mandatory marked in{" "}
                      <span className="text-red-500">red</span>)
                    </span>
                    <div className="h-50 border-2 rounded-md my-5 overflow-y-auto max-w-full scroll-visible">
                      <ul className="p-4">
                        {serviceRules?.map((sr) =>
                          sr.rules
                            ?.find((data) => data?.name === subRule)
                            ?.mandatoryFormats?.map(
                              (mandatoryFormat, index) =>
                                mandatoryFormat &&
                                getFormats(mandatoryFormat, index),
                            ),
                        )}
                        {serviceRules?.map((sr) =>
                          sr.rules
                            ?.find((data) => data?.name === subRule)
                            ?.nonMandatoryFormats?.map(
                              (nonMandatoryFormat, index) =>
                                nonMandatoryFormat &&
                                getFormats(nonMandatoryFormat, index, true),
                            ),
                        )}
                      </ul>
                    </div>
                  </div>
                )}
                {subRule && (
                  <div className="flex-1">
                    <span className="text-lg font-semibold overflow-wrap break-words">
                      Sequences (mandatory marked in{" "}
                      <span className="text-red-500">red</span>)
                    </span>
                    <div className="h-50 border-2 rounded-md my-5 overflow-y-auto max-w-full scroll-visible">
                      <ul className="p-4">
                        {serviceRules.map((sr, srIndex) =>
                          sr.rules
                            ?.find((data) => data?.name === subRule)
                            ?.mandatorySequences?.map((ms, msIndex) => (
                              <li
                                key={`mandatory-sequence-${srIndex}-${msIndex}`}
                                className="flex items-center mb-1 text-red-500 max-w-full overflow-wrap break-words"
                              >
                                <span>{ms?.name}</span>
                                {ms.allowedFormats &&
                                ms.allowedFormats.length ? (
                                  <span className="ml-1">
                                    {" "}
                                    ({ms.allowedFormats.join(", ")})
                                  </span>
                                ) : null}
                                {ms?.description && (
                                  <span className="ml-2 flex-shrink-0">
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <InfoIcon className="w-4 h-4" />
                                        </TooltipTrigger>
                                        <TooltipContent
                                          side="bottom"
                                          className="!p-2 !bg-primary border border-accent-foreground max-w-xs overflow-wrap break-words"
                                        >
                                          {ms.description}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </span>
                                )}
                              </li>
                            )),
                        )}
                        {serviceRules.map((sr, srIndex) =>
                          sr.rules
                            ?.find((data) => data?.name === subRule)
                            ?.nonMandatorySequences?.map((nms, nmsIndex) => (
                              <li
                                key={`non-mandatory-sequence-${srIndex}-${nmsIndex}`}
                                className="flex items-center mb-1 text-gray-500 max-w-full overflow-wrap break-words"
                              >
                                <span>{nms?.name}</span>
                                {nms.allowedFormats &&
                                nms.allowedFormats.length ? (
                                  <span className="ml-1">
                                    {" "}
                                    ({nms.allowedFormats.join(", ")})
                                  </span>
                                ) : null}
                                {nms?.description && (
                                  <span className="ml-2 flex-shrink-0">
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <InfoIcon className="w-4 h-4" />
                                        </TooltipTrigger>
                                        <TooltipContent
                                          side="bottom"
                                          className="!p-2 !bg-primary border border-accent-foreground max-w-xs overflow-wrap break-words"
                                        >
                                          {nms.description}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </span>
                                )}
                              </li>
                            )),
                        )}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
              <Button
                className="text-paint ring-paint ring-1 mb-8"
                size="sm"
                onClick={() => {
                  setSelectedDelivery({
                    taskShotId: autoNamingRule.id,
                    selectedSubrule: subRule,
                    serviceName: formatServiceName(
                      autoNamingRule.task.service.name,
                    ),
                    projectId,
                  });
                  setOpenNameValidationModal(true);
                }}
                disabled={!subRule}
              >
                Check {formatServiceName(autoNamingRule.task.service.name)}{" "}
                Tasks
              </Button>
            </div>
          </div>
        );
      })}
      {openNameValidationModal && selectedDelivery && (
        <CheckDeliveryModal
          openNameValidationModal={openNameValidationModal}
          setOpenNameValidationModal={(open) => {
            if (!open) setSelectedDelivery(null);
            setOpenNameValidationModal(open);
          }}
          serviceName={selectedDelivery.serviceName}
          projectId={selectedDelivery.projectId}
          taskShotId={selectedDelivery.taskShotId}
          taskShotIds={taskShotIds}
          selectedSubrule={selectedDelivery.selectedSubrule}
        />
      )}
    </div>
  );
};

const NameValidator = () => {
  const { currentProject } = useUserContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [openNameValidationModal, setOpenNameValidationModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<{
    taskShotId: string;
    selectedSubrule?: string;
    serviceName: string;
    projectId: string;
  } | null>(null);

  const taskShotIds = getArrayFilterFromParams(
    searchParams.get(TASK_SHOTS_QUERY_PARAM),
  );

  const { data, loading, error } = useQuery<
    AutoNamingRulesForTaskShotQuery,
    AutoNamingRulesForTaskShotQueryVariables
  >(AUTO_NAMING_RULES_QUERY, {
    variables: { taskShotIds },
    fetchPolicy: "network-only",
  });

  if (!currentProject) return null;
  if (loading) return <Loader />;

  const redirectToDeliverPage = () => {
    if (currentProject)
      navigate({
        pathname: generatePath(ACTIONS_URL, {
          projectId: currentProject.id,
          action: ActionsType.DELIVER_TASKS,
        }),
        search: `?${createSearchParams({ [TASK_SHOTS_QUERY_PARAM]: taskShotIds.join(",") })}`,
      });
  };

  if (
    error ||
    !data?.autoNamingRulesForTaskShot ||
    data.autoNamingRulesForTaskShot.length === 0
  ) {
    redirectToDeliverPage();
    return null;
  }

  const filteredTaskShots = data.autoNamingRulesForTaskShot.filter(
    (ts): ts is NonNullable<typeof ts> => ts !== null,
  );

  if (!filteredTaskShots.length) {
    redirectToDeliverPage();
    return null;
  }

  const firstTaskShot = filteredTaskShots[0];
  const serviceRules: ServiceRule[] = (firstTaskShot.serviceRules ?? []).filter(
    (sr): sr is ServiceRule => sr !== null,
  );

  return (
    <div className="p-6 max-w-full">
      <div className="flex items-center justify-start gap-5 max-w-full">
        <span className="font-semibold text-xl overflow-wrap break-words">
          Confirm Deliver Task
        </span>
        <ProjectSelector />
      </div>

      <div className="bg-accordion rounded-2xl mt-5 overflow-hidden max-w-full">
        <div className="p-5">
          <span className="text-xl overflow-wrap break-words">
            Name Validation
          </span>
        </div>
        <div className="mt-5 bg-popover p-5 mb-5">
          <div className="bg-secondary flex gap-10 p-5 rounded-xl max-w-full">
            <div className="flex-shrink-0">
              <NameValidationIcon color="var(--foreground)" />
            </div>
            <div className="flex flex-col gap-3 max-w-full">
              <div className="overflow-wrap break-words">
                <span className="text-secondary-foreground">
                  {serviceRules.length > 0 && serviceRules[0].isDefault
                    ? "Default "
                    : "Custom "}{" "}
                  Naming conventions are enabled for project{" "}
                </span>
                <span className="font-bold">{currentProject.name}</span>
              </div>
              <div className="text-secondary-foreground overflow-wrap break-words">
                You have selected tasks which need to follow Hotspring's naming
                specification
              </div>
              <div className="font-bold overflow-wrap break-words">
                Please check your delivery packages before proceeding
              </div>
            </div>
          </div>
          <div className="max-w-full">
            <NameValidatorList
              filteredTaskShots={filteredTaskShots}
              taskShotIds={taskShotIds}
              openNameValidationModal={openNameValidationModal}
              setOpenNameValidationModal={setOpenNameValidationModal}
              selectedDelivery={selectedDelivery}
              setSelectedDelivery={setSelectedDelivery}
              projectId={
                currentProject.subcontractedProject
                  ? currentProject.subcontractedProject.id
                  : currentProject.id
              }
            />
          </div>
        </div>

        <div className=" max-w-full border-t-2 border-accent-foreground">
          <div className="flex items-center justify-between bg-primary p-5">
            <div className="overflow-wrap break-words">
              I have checked the naming convention of my tasks
            </div>
            <div className="flex gap-5">
              <Button
                onClick={() => {
                  navigate(-1);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-paint text-white hover:bg-paint/80"
                onClick={() => {
                  navigate({
                    pathname: generatePath(ACTIONS_URL, {
                      projectId: currentProject.id,
                      action: ActionsType.DELIVER_TASKS,
                    }),
                    search: `?${createSearchParams({ [TASK_SHOTS_QUERY_PARAM]: taskShotIds.join(",") })}`,
                  });
                }}
              >
                Proceed
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NameValidator;
